import { create } from 'zustand';
import supabase from '@/lib/supabase';
import type {
  BillingInterval,
  GatedFeature,
  SubscriptionStatus,
  SubscriptionTier,
} from '@/constant/subscription';
import { apiClient } from '@/lib/api-client';
import {
  mapLegacyPlanToInterval,
  normalizeSubscriptionTier,
} from '@/constant/subscription';
import useAuthStore from '@/store/useAuthStore';
import { getCanonicalProfileRole } from '@/lib/profile-role';

export interface Subscription {
  id: string;
  parent_id: string | null;
  student_id: string;
  stripe_subscription_id: string;
  stripe_customer_id?: string | null;
  stripe_subscription_item_id?: string | null;
  tier: SubscriptionTier;
  billing_interval: BillingInterval;
  plan?: string;
  status: SubscriptionStatus;
  cancel_at_period_end: boolean;
  current_period_start: string | null;
  current_period_end: string | null;
  is_trial?: boolean;
  manager_type?: 'parent' | 'student' | null;
  manager_profile_id?: string | null;
  management_locked?: boolean;
  management_unlock_at?: string | null;
}

export function getCurrentTier(): SubscriptionTier {
  // Teacher preview mode: treat as Plus tier so teachers can experience student view
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const preview = params.get('teacherPreview');
    if (preview === 'plus' || preview === 'max') {
      return preview as SubscriptionTier;
    }
  }

  const authProfile = useAuthStore.getState().profile;
  if (getCanonicalProfileRole(authProfile) === 'teacher') {
    return 'plus';
  }

  const { subscription } = useSubscriptionStore.getState();
  if (!subscription || (subscription.status !== 'active' && subscription.status !== 'past_due')) {
    return 'free';
  }
  // Trial subscriptions expire based on current_period_end
  if (subscription.is_trial && subscription.current_period_end) {
    if (new Date(subscription.current_period_end) < new Date()) {
      return 'free';
    }
  }
  return subscription.tier;
}

interface UsageQuota {
  feature: string;
  used_count: number;
  period_start: string;
}

interface SubscriptionState {
  subscription: Subscription | null;
  quotas: UsageQuota[];
  loading: boolean;
  initialized: boolean;
  loadForStudent: (studentId: string) => Promise<void>;
  consumeQuota: (feature: GatedFeature, studentId: string) => Promise<{ allowed: boolean; used?: number }>;
  subscribeRealtime: (studentId: string) => () => void;
  reset: () => void;
}

const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  subscription: null,
  quotas: [],
  loading: false,
  initialized: false,

  loadForStudent: async (studentId: string) => {
    set({ loading: true });
    try {
      const [subResult, quotaResult] = await Promise.all([
        supabase
          .from('subscriptions')
          .select('*')
          .eq('student_id', studentId)
          .in('status', ['active', 'past_due'])
          .maybeSingle(),
        supabase
          .from('usage_quotas')
          .select('feature, used_count, period_start')
          .eq('student_id', studentId),
      ]);

      // Filter out expired trials
      const subData = subResult.data;
      const isExpiredTrial = subData?.is_trial
        && subData.current_period_end
        && new Date(subData.current_period_end) < new Date();

      set({
        subscription: subData && !isExpiredTrial
          ? {
              ...subData,
              tier: normalizeSubscriptionTier((subData as { tier?: string | null }).tier),
              billing_interval:
                (subData as { billing_interval?: BillingInterval | null }).billing_interval ||
                mapLegacyPlanToInterval((subData as { plan?: string | null }).plan),
            }
          : null,
        quotas: quotaResult.data || [],
        loading: false,
        initialized: true,
      });
    } catch {
      set({ loading: false, initialized: true });
    }
  },

  consumeQuota: async (feature: GatedFeature, studentId: string) => {
    try {
      const res = await apiClient.post('/api/stripe/consume-quota', { feature });
      const data = await res.json();
      await get().loadForStudent(studentId);
      return { allowed: data.allowed !== false, used: data.used };
    } catch {
      return { allowed: true };
    }
  },

  subscribeRealtime: (studentId: string) => {
    const channel = supabase
      .channel(`subscription:${studentId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `student_id=eq.${studentId}`,
        },
        () => {
          get().loadForStudent(studentId);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  },

  reset: () => {
    set({ subscription: null, quotas: [], loading: false, initialized: false });
  },
}));

export default useSubscriptionStore;
